/**
 * Wishlist — self-contained storefront wishlist.
 *
 * Replaces the Swym integration (which never finished initialising: _swat loaded
 * but retailerSettings/apiEndpoint stayed empty, so no plugin DOM and no anchor
 * was ever injected) and takes over from the jQuery handler in theme.js.
 *
 * Storage layers:
 *   - localStorage 'drc:wishlist'  — authoritative on the storefront.
 *   - localStorage 'wishlistItem'  — legacy array of handles, mirrored on every
 *     write. theme.js/toolbar.js still read it in seven places; mirroring keeps
 *     them consistent instead of fighting them.
 *   - customer.metafields.custom.wishlist — read-only here. Liquid seeds it into
 *     WishlistConfig.seed and we merge it in. Writes belong to the customer
 *     account extension; the Online Store has no native write path.
 *
 * theme.js still binds to [data-wishlist]; this module binds to
 * [data-wishlist-toggle] so only one handler ever runs per click.
 */
(function () {
  'use strict';

  var KEY = 'drc:wishlist';
  var LEGACY_KEY = 'wishlistItem';
  var VERSION = 1;
  var MAX_ITEMS = 200;
  var UNDO_MS = 6000;

  var cfg = window.WishlistConfig || {};
  var t = cfg.strings || {};

  /* ---------------------------------------------------------------- store -- */

  /**
   * Shape: { p: productId, v: variantId|null, h: handle, t: addedAtSeconds }
   *
   * Keyed by product id — not by product+variant, and not by handle as the old
   * code did (a handle rename silently orphaned the entry).
   *
   * One entry per product is deliberate: the UI only ever exposes a single heart
   * per product — on a card, in the header count, on the wishlist grid — so a
   * second row for another variant would render as a duplicate product with
   * nothing to tell the two apart. The selected variant is still recorded on the
   * entry and travels to the customer metafield; saving the same product from a
   * different variant updates that value instead of adding a row.
   */
  function itemKey(productId) {
    return String(productId);
  }

  function now() {
    return Math.floor(Date.now() / 1000);
  }

  function safeParse(raw) {
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch (e) {
      return null;
    }
  }

  var Store = {
    _cache: null,
    _bus: new EventTarget(),

    /** Normalise anything we read into a clean { v, items, updated } record. */
    _normalise: function (data) {
      var items = [];
      var seen = Object.create(null);

      if (data && Array.isArray(data.items)) {
        data.items.forEach(function (raw) {
          if (!raw || raw.p == null) return;
          var v = raw.v == null || raw.v === '' ? null : Number(raw.v);
          var k = itemKey(raw.p);
          // Collapses any duplicate rows left behind by an older build.
          if (seen[k]) return;
          seen[k] = true;
          items.push({
            p: Number(raw.p),
            v: v,
            h: typeof raw.h === 'string' ? raw.h : '',
            t: Number(raw.t) || now()
          });
        });
      }

      items.sort(function (a, b) {
        return b.t - a.t;
      });

      return { v: VERSION, items: items.slice(0, MAX_ITEMS), updated: (data && Number(data.updated)) || now() };
    },

    /** Older builds stored a bare array of handles under 'wishlistItem'. */
    _migrateLegacy: function () {
      var legacy = safeParse(localStorage.getItem(LEGACY_KEY));
      if (!Array.isArray(legacy) || !legacy.length) return null;

      var stamp = now();
      return this._normalise({
        items: legacy
          .filter(function (h) {
            return typeof h === 'string' && h;
          })
          .map(function (h, i) {
            // No id available in the legacy format: negative synthetic ids keep
            // the entries addressable until the handle resolves on next render.
            return { p: -(i + 1), v: null, h: h, t: stamp - i };
          })
      });
    },

    read: function () {
      if (this._cache) return this._cache;

      var data = safeParse(this._readRaw(KEY));
      if (!data) data = this._migrateLegacy();

      this._cache = this._normalise(data || {});
      return this._cache;
    },

    _readRaw: function (key) {
      try {
        return localStorage.getItem(key);
      } catch (e) {
        return null;
      }
    },

    write: function (record) {
      this._cache = this._normalise(record);
      this._cache.updated = now();

      try {
        localStorage.setItem(KEY, JSON.stringify(this._cache));
        // Mirror for the jQuery helpers still living in theme.js.
        localStorage.setItem(
          LEGACY_KEY,
          JSON.stringify(
            this._cache.items
              .map(function (i) {
                return i.h;
              })
              .filter(Boolean)
          )
        );
      } catch (e) {
        // Quota or private mode — keep the in-memory copy so the page still works.
      }

      this._bus.dispatchEvent(new CustomEvent('change', { detail: this._cache }));
      return this._cache;
    },

    items: function () {
      return this.read().items;
    },

    count: function () {
      return this.read().items.length;
    },

    has: function (productId) {
      var k = itemKey(productId);
      return this.read().items.some(function (i) {
        return itemKey(i.p) === k;
      });
    },

    get: function (productId) {
      var k = itemKey(productId);
      return (
        this.read().items.filter(function (i) {
          return itemKey(i.p) === k;
        })[0] || null
      );
    },

    /** Upsert: an existing product keeps its slot but adopts the new variant. */
    add: function (item) {
      var next = {
        p: Number(item.p),
        v: item.v == null ? null : Number(item.v),
        h: item.h || '',
        t: now()
      };

      var items = this.read().items.filter(function (i) {
        return itemKey(i.p) !== itemKey(next.p);
      });

      items.unshift(next);
      return this.write({ items: items });
    },

    remove: function (productId) {
      var k = itemKey(productId);
      var items = this.read().items.filter(function (i) {
        return itemKey(i.p) !== k;
      });
      return this.write({ items: items });
    },

    /** Union merge, newest timestamp wins. Used for the customer metafield seed. */
    merge: function (incoming) {
      if (!Array.isArray(incoming) || !incoming.length) return this.read();

      var byKey = Object.create(null);
      this.read().items.forEach(function (i) {
        byKey[itemKey(i.p)] = i;
      });

      incoming.forEach(function (raw) {
        if (!raw || raw.p == null) return;
        var v = raw.v == null || raw.v === '' ? null : Number(raw.v);
        var k = itemKey(raw.p);
        var candidate = { p: Number(raw.p), v: v, h: raw.h || '', t: Number(raw.t) || now() };
        if (!byKey[k] || candidate.t > byKey[k].t) byKey[k] = candidate;
      });

      return this.write({
        items: Object.keys(byKey).map(function (k) {
          return byKey[k];
        })
      });
    },

    on: function (fn) {
      this._bus.addEventListener('change', fn);
    }
  };

  /* --------------------------------------------------------- announcements -- */

  /**
   * base.css carries `*:empty { display: none }`, so an empty live region or
   * badge collapses out of the layout. Every node these helpers touch keeps a
   * zero-width space rather than an empty text node.
   */
  var ZWSP = '​';

  /**
   * Composed in JS rather than through a `{{ count }}` placeholder: the Liquid
   * `t` filter would need the count at render time, which we don't have.
   */
  function countText(n) {
    return n === 1 ? t.count_one || '1 item' : n + ' ' + (t.count_many || 'items');
  }

  var live = null;
  function announce(message) {
    if (!live) {
      live = document.getElementById('WishlistLiveRegion');
      if (!live) return;
    }
    live.textContent = ZWSP;
    // Re-assigning in a new frame makes screen readers re-announce a repeat message.
    window.requestAnimationFrame(function () {
      live.textContent = message;
    });
  }

  var toastTimer = null;
  function toast(message, undoFn) {
    var host = document.getElementById('WishlistToast');
    if (!host) return;

    window.clearTimeout(toastTimer);
    host.innerHTML = '';

    var text = document.createElement('span');
    text.className = 'wishlist-toast__text';
    text.textContent = message;
    host.appendChild(text);

    if (undoFn) {
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'wishlist-toast__undo';
      btn.textContent = t.undo || 'Undo';
      btn.addEventListener('click', function () {
        undoFn();
        hideToast();
      });
      host.appendChild(btn);
    }

    host.hidden = false;
    host.classList.add('is-visible');
    toastTimer = window.setTimeout(hideToast, UNDO_MS);
  }

  function hideToast() {
    var host = document.getElementById('WishlistToast');
    if (!host) return;
    host.classList.remove('is-visible');
    host.hidden = true;
    host.innerHTML = ZWSP;
  }

  /* -------------------------------------------------------------- buttons -- */

  /**
   * Resolve the variant a trigger refers to.
   *
   * Only PDP hearts carry data-variant-live; they follow the variant picker so a
   * saved item points at the version actually selected. The lookup is scoped to
   * the product container on purpose — the first `form[action*="/cart/add"]` on
   * the page belongs to the header quick search, and reading `[name="id"]` from
   * the document would pick up that form's value instead of the product's.
   */
  function resolveVariant(el) {
    var explicit = el.getAttribute('data-variant-id');
    var fallback = explicit && explicit !== '' ? Number(explicit) : null;

    if (!el.hasAttribute('data-variant-live')) return fallback;

    var scope = el.closest('.productView, product-info, .product-single, .featured-product');
    if (!scope) scope = el.closest('form[action*="/cart/add"]');
    if (!scope) return fallback;

    var input = scope.querySelector('form[action*="/cart/add"] [name="id"]') || scope.querySelector('[name="id"]');
    var value = input && input.value;

    return value ? Number(value) : fallback;
  }

  function readTrigger(el) {
    return {
      p: el.getAttribute('data-product-id'),
      v: resolveVariant(el),
      h: el.getAttribute('data-wishlist-handle') || ''
    };
  }

  function isTriggerActive(el) {
    var productId = el.getAttribute('data-product-id');
    return productId ? Store.has(productId) : false;
  }

  function paintTrigger(el) {
    var active = isTriggerActive(el);

    el.classList.toggle('wishlist-added', active);
    el.classList.toggle('is-active', active);

    if (el.tagName === 'BUTTON' || el.getAttribute('role') === 'button') {
      el.setAttribute('aria-pressed', active ? 'true' : 'false');
    }

    var label = active ? t.added || 'Added to wishlist' : t.add || 'Add to wishlist';
    el.setAttribute('aria-label', label);
    el.setAttribute('title', label);

    var text = el.querySelector('.text');
    if (text) text.textContent = label;
  }

  function paintAllTriggers() {
    document.querySelectorAll('[data-wishlist-toggle]').forEach(paintTrigger);
  }

  function onTriggerClick(event) {
    var el = event.target.closest('[data-wishlist-toggle]');
    if (!el) return;

    event.preventDefault();
    event.stopPropagation();

    var d = readTrigger(el);
    if (!d.p) return;

    var active = isTriggerActive(el);

    if (active) {
      // Kept for undo — restoring must bring back the variant that was saved,
      // which is not necessarily the one selected right now.
      var previous = Store.get(d.p);
      Store.remove(d.p);

      announce(t.removed || 'Removed from wishlist');
      toast(t.removed || 'Removed from wishlist', function () {
        if (previous) Store.merge([previous]);
        announce(t.added || 'Added to wishlist');
      });
    } else {
      Store.add(d);
      announce(t.added || 'Added to wishlist');
      toast(t.added || 'Added to wishlist');
    }

    // Reflect on every heart pointing at the same product, not just this one.
    document.querySelectorAll('[data-wishlist-toggle][data-product-id="' + d.p + '"]').forEach(paintTrigger);
    el.classList.remove('wishlist-pulse');
    void el.offsetWidth;
    el.classList.add('wishlist-pulse');
  }

  /* ---------------------------------------------------------------- badge -- */

  class WishlistCount extends HTMLElement {
    connectedCallback() {
      this.render();
      Store.on(this.render.bind(this));
    }

    render() {
      var n = Store.count();
      this.textContent = n > 0 ? String(n) : ZWSP;
      this.hidden = n === 0;
      this.setAttribute('aria-hidden', n === 0 ? 'true' : 'false');

      var link = this.closest('[data-wishlist-link]');
      if (link) {
        var title = t.title || 'Wishlist';
        link.setAttribute('aria-label', n === 0 ? title : title + ', ' + countText(n));
      }
    }
  }

  /* ----------------------------------------------------------------- page -- */

  class WishlistPage extends HTMLElement {
    connectedCallback() {
      this.grid = this.querySelector('[data-wishlist-grid]');
      this.emptyEl = this.querySelector('[data-wishlist-empty]');
      this.toolbar = this.querySelector('[data-wishlist-toolbar]');
      this.countEl = this.querySelector('[data-wishlist-page-count]');
      this.perPage = Number(this.getAttribute('data-per-page')) || 24;
      this.page = 1;

      this.querySelectorAll('[data-col]').forEach(
        function (btn) {
          btn.addEventListener('click', this.onColClick.bind(this));
        }.bind(this)
      );

      Store.on(this.onStoreChange.bind(this));
      this.render();
    }

    onStoreChange() {
      // A removal on the last item of a page should not strand the viewer.
      var maxPage = Math.max(1, Math.ceil(Store.count() / this.perPage));
      if (this.page > maxPage) this.page = maxPage;
      this.render();
    }

    /**
     * Mirrors the theme's own switcher (assets/toolbar.js) so the same CSS
     * rules apply here as on a collection page: list mode is `productList`
     * with no column class, grid mode is `productGrid` plus exactly one
     * `column-N`.
     */
    onColClick(event) {
      var btn = event.currentTarget;
      var col = Number(btn.getAttribute('data-col'));

      this.querySelectorAll('[data-col]').forEach(function (b) {
        b.classList.toggle('active', b === btn);
        b.setAttribute('aria-pressed', b === btn ? 'true' : 'false');
      });

      this.grid.classList.remove('productList', 'productGrid', 'column-2', 'column-3', 'column-4', 'column-5');

      if (col === 1) {
        this.grid.classList.add('productList');
      } else {
        this.grid.classList.add('productGrid', 'column-' + col);
      }
    }

    async render() {
      var items = Store.items();

      if (this.countEl) {
        this.countEl.textContent = items.length ? countText(items.length) : ZWSP;
      }

      if (!items.length) {
        this.grid.innerHTML = '';
        this.grid.hidden = true;
        if (this.toolbar) this.toolbar.hidden = true;
        if (this.emptyEl) this.emptyEl.hidden = false;
        this.renderPagination(0);
        return;
      }

      this.grid.hidden = false;
      if (this.toolbar) this.toolbar.hidden = false;
      if (this.emptyEl) this.emptyEl.hidden = true;

      var start = (this.page - 1) * this.perPage;
      var slice = items.slice(start, start + this.perPage);

      this.grid.setAttribute('aria-busy', 'true');
      var cards = await this.fetchCards(slice);
      this.grid.setAttribute('aria-busy', 'false');

      this.grid.innerHTML = '';
      cards.forEach(
        function (html, i) {
          if (!html) return;

          // The card markup already opens with `.product`, which is what the
          // theme's grid rules size and space. Appending it directly — rather
          // than inside a wrapper div — keeps `.product` as the grid item, the
          // same as on a collection page.
          var parsed = document.createElement('div');
          parsed.innerHTML = html;

          var product = parsed.querySelector('.product') || parsed.firstElementChild;
          if (!product) return;

          product.setAttribute('data-wishlist-item', itemKey(slice[i].p));
          this.grid.appendChild(product);
        }.bind(this)
      );

      paintAllTriggers();
      this.renderPagination(items.length);
    }

    /**
     * Cards come from the theme's own ajax_wishlist_card view so the wishlist
     * looks identical to every other product grid. Batched to keep a large
     * wishlist from opening 200 sockets at once.
     */
    async fetchCards(items) {
      var root = (window.routes && window.routes.root) || '';
      var out = new Array(items.length);
      var batch = 6;

      for (var i = 0; i < items.length; i += batch) {
        var chunk = items.slice(i, i + batch);
        var results = await Promise.all(
          chunk.map(function (item) {
            if (!item.h) return Promise.resolve(null);
            return fetch(root + '/products/' + item.h + '?view=ajax_wishlist_card')
              .then(function (res) {
                return res.ok ? res.text() : null;
              })
              .catch(function () {
                return null;
              });
          })
        );
        results.forEach(function (html, j) {
          out[i + j] = html;
        });
      }

      // A handle that no longer resolves means the product is gone — drop it so
      // the list cannot accumulate invisible dead entries.
      items.forEach(function (item, i) {
        if (out[i] === null && item.h) Store.remove(item.p);
      });

      return out;
    }

    renderPagination(total) {
      var host = this.querySelector('[data-wishlist-pagination]');
      if (!host) return;

      var pages = Math.ceil(total / this.perPage);
      if (pages <= 1) {
        host.innerHTML = ZWSP;
        host.hidden = true;
        return;
      }

      host.hidden = false;
      host.innerHTML = '';

      for (var n = 1; n <= pages; n++) {
        var li = document.createElement('li');
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'wishlist-pagination__item' + (n === this.page ? ' is-current' : '');
        btn.textContent = String(n);
        btn.setAttribute('aria-label', 'Page ' + n);
        if (n === this.page) btn.setAttribute('aria-current', 'page');
        btn.addEventListener(
          'click',
          function (page) {
            this.page = page;
            this.render();
            this.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }.bind(this, n)
        );
        li.appendChild(btn);
        host.appendChild(li);
      }
    }
  }

  /* ----------------------------------------------------------------- boot -- */

  function init() {
    // Seed from the customer metafield before anything paints, so a signed-in
    // customer sees their saved items on first render rather than after a flash.
    if (cfg.seed && cfg.seed.length) Store.merge(cfg.seed);

    document.addEventListener('click', onTriggerClick);
    paintAllTriggers();

    // Cards injected by filters, quick view and infinite scroll need repainting.
    Store.on(paintAllTriggers);
    document.addEventListener('halo:products:loaded', paintAllTriggers);

    var mo = new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        for (var j = 0; j < records[i].addedNodes.length; j++) {
          var node = records[i].addedNodes[j];
          if (node.nodeType !== 1) continue;
          if (node.matches && node.matches('[data-wishlist-toggle]')) {
            paintTrigger(node);
          } else if (node.querySelector && node.querySelector('[data-wishlist-toggle]')) {
            node.querySelectorAll('[data-wishlist-toggle]').forEach(paintTrigger);
          }
        }
      }
    });
    mo.observe(document.body, { childList: true, subtree: true });

    // Another tab changing the wishlist should not leave this one stale.
    window.addEventListener('storage', function (e) {
      if (e.key !== KEY) return;
      Store._cache = null;
      paintAllTriggers();
      document.querySelectorAll('wishlist-count, wishlist-page').forEach(function (el) {
        if (el.render) el.render();
      });
    });

    if (!customElements.get('wishlist-count')) customElements.define('wishlist-count', WishlistCount);
    if (!customElements.get('wishlist-page')) customElements.define('wishlist-page', WishlistPage);
  }

  window.Wishlist = { store: Store, paint: paintAllTriggers };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
